import Vue from 'vue';
import { getEffectivePixelRatio } from '../utils';

// Calculate once, share across all component instances
const effectivePixelRatio = getEffectivePixelRatio();

Vue.component('responsive-image', {
    name: 'ResponsiveImage',

    props: {
        // Image sources
        srcsetWebp: {
            type: String,
            default: null,
        },
        srcsetJpeg: {
            type: String,
            default: null,
        },

        // Alternative: pass a complete image object
        image: {
            type: Object,
            default: null,
            validator(obj) {
                return !obj || obj.srcSetWebp || obj.srcSetJpeg;
            },
        },

        // Responsive sizing
        sizes: {
            type: String,
            default: null,
        },
        variant: {
            type: String,
            default: 'default',
            validator(value) {
                return ['default', 'hero', 'slider', 'content'].includes(value);
            },
        },

        // High-DPI support
        enableDpr: {
            type: Boolean,
            default: false,
        },

        // Styling
        alt: {
            type: String,
            default: 'Image',
        },
        imageClass: {
            type: [String, Array, Object],
            default: null,
        },
        wrapperClass: {
            type: [String, Array, Object],
            default: null,
        },

        // Optional link wrapper
        href: {
            type: String,
            default: null,
        },
        target: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            devicePixelRatio: effectivePixelRatio,
        };
    },

    computed: {
        effectiveSrcsetWebp() {
            const srcset = this.image?.srcSetWebp || this.srcsetWebp;
            return this.addDprToSrcset(srcset);
        },

        effectiveSrcsetJpeg() {
            const srcset = this.image?.srcSetJpeg || this.srcsetJpeg;
            return this.addDprToSrcset(srcset);
        },

        fallbackSrcset() {
            return this.effectiveSrcsetJpeg || this.effectiveSrcsetWebp;
        },

        computedSizes() {
            if (this.sizes) return this.sizes;

            const sizeMap = {
                hero: '(max-width: 768px) 100vw, (max-width: 1280px) 100vw, 1920px',
                slider: '(max-width: 768px) 90vw, (max-width: 1280px) 60vw, 960px',
                content: '(max-width: 900px) 90vw, 800px',
                default:
                    '(max-width: 768px) 100vw, (max-width: 1280px) 90vw, 1280px',
            };

            return sizeMap[this.variant];
        },

        linkProps() {
            if (!this.href) return {};

            return {
                href: this.href,
                target: this.target,
                draggable: false,
            };
        },
    },

    methods: {
        /**
         * Add DPR parameter to srcset URLs when enabled
         * @param {string} srcset - The srcset string
         * @return {string} Modified srcset with dpr parameter
         */
        addDprToSrcset(srcset) {
            if (!srcset || !this.enableDpr) return srcset;

            // Split by ", http" to handle commas in URL params (e.g., auto=compress,format)
            const entries = srcset.split(/, (?=https?:\/\/)/);

            const modifiedEntries = entries.map((entry) =>
                this.addDprToEntry(entry),
            );

            return modifiedEntries.join(', ');
        },

        /**
         * Add DPR parameter to a single srcset entry
         * @param {string} entry - Single srcset entry (e.g., "url 640w")
         * @return {string} Modified entry with dpr parameter
         */
        addDprToEntry(entry) {
            const parts = entry.trim().split(/\s+/);
            const url = parts[0];
            const descriptor = parts.slice(1).join(' ');

            // Skip if already has dpr parameter
            if (url.includes('dpr=')) return entry;

            const separator = url.includes('?') ? '&' : '?';
            const modifiedUrl = `${url}${separator}dpr=2`;

            return descriptor ? `${modifiedUrl} ${descriptor}` : modifiedUrl;
        },
    },

    template: `
    <component :is="href ? 'a' : 'div'" v-bind="linkProps" :class="wrapperClass">
      <picture>
        <source v-if="effectiveSrcsetWebp" type="image/webp" :srcset="effectiveSrcsetWebp" :sizes="computedSizes" />
        <source v-if="effectiveSrcsetJpeg" :srcset="effectiveSrcsetJpeg" :sizes="computedSizes" />
        <img :srcset="fallbackSrcset" :sizes="computedSizes" :alt="alt" :class="imageClass" loading="lazy" decoding="async" draggable="false" />
      </picture>
    </component>
    `,
});
